import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

/** Promoter — own monthly attendance summary (mobile). */
export default function PromoterMyAttendance() {
  const [data, setData] = useState(null);
  const [month] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    api.get("/promoter/attendance/me", { params: { month } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [month]);

  if (!data) return <div className="text-sm text-slate-500" data-testid="my-attendance-loading">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="my-attendance-page">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Attendance</h1>
        <p className="text-xs text-slate-500 mt-0.5">{data.month}</p>
      </header>

      <div className="grid grid-cols-2 gap-2" data-testid="my-attendance-summary">
        <Tile label="Present" value={data.summary.present_days} testid="att-present" />
        <Tile label="Absent" value={data.summary.absent_days} testid="att-absent" />
        <Tile label="Late check-ins" value={data.summary.late_check_ins} testid="att-late" />
        <Tile label="Early check-outs" value={data.summary.early_check_outs} testid="att-early" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="my-attendance-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">In</th>
                  <th className="text-left px-2 py-1.5">Out</th>
                  <th className="text-left px-2 py-1.5">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-slate-400 py-6" data-testid="my-attendance-empty">
                    No attendance records yet this month.
                  </td></tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`my-attendance-row-${r.date}`}>
                    <td className="px-2 py-1.5 font-mono">{r.date}</td>
                    <td className="px-2 py-1.5">{r.check_in_at ? fmtTime(r.check_in_at) : "—"}</td>
                    <td className="px-2 py-1.5">{r.check_out_at ? fmtTime(r.check_out_at) : "—"}</td>
                    <td className="px-2 py-1.5 space-x-1">
                      {r.check_in_late && <Pill color="rose">Late</Pill>}
                      {r.check_out_early && <Pill color="amber">Early</Pill>}
                      {r.absent && <Pill color="slate">Absent</Pill>}
                      {r.manual_override && <Pill color="indigo">Override</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function Pill({ children, color }) {
  const cls = {
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-800",
    slate: "bg-slate-200 text-slate-700",
    indigo: "bg-indigo-100 text-indigo-800",
  }[color] || "bg-slate-100 text-slate-700";
  return <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
